// components/DarkModeSwitch.tsx
"use client";
import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { motion, AnimatePresence } from "motion/react";
import { HugeiconsIcon } from "@hugeicons/react";
import { Sun01Icon, Moon02Icon } from "@hugeicons/core-free-icons";

let isDark = false;

// Custom minimalist Monitor/System icon matching Hugeicons stroke style
const SystemIcon = ({ className, size = 16 }: { className?: string; size?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    style={{ width: size, height: size }}
  >
    <rect x="3" y="4" width="18" height="12" rx="2" />
    <path d="M9 20h6" />
    <path d="M12 16v4" />
  </svg>
);

const DarkModeSwitch = ({ width }: { width?: string }) => {
  const [mounted, setMounted] = useState(false);
  const { theme, setTheme, resolvedTheme } = useTheme();

  useEffect(() => {
    setMounted(true);
  }, []);

  const isDarkMode = resolvedTheme === "dark";

  // Keep legacy global variable synchronized for compatibility
  useEffect(() => {
    isDark = isDarkMode;
  }, [isDarkMode]);

  if (!mounted) {
    return (
      <div className="h-9 w-[100px] rounded-full bg-neutral-200/30 dark:bg-zinc-800/30 animate-pulse border border-neutral-200/20 dark:border-zinc-800/20" />
    );
  }

  const isSmall = width === "w-4";

  return (
    <div
      className={`relative flex items-center justify-between bg-neutral-100/50 dark:bg-zinc-900/50 border border-neutral-200/40 dark:border-zinc-800/60 rounded-full p-1 backdrop-blur-md select-none ${
        isSmall ? "h-7 w-[78px]" : "h-9 w-[100px]"
      }`}
    >
      {/* Light Mode Button */}
      <button
        onClick={() => setTheme("light")}
        aria-label="Switch to Light Mode"
        className={`relative z-10 flex items-center justify-center rounded-full transition-colors cursor-pointer group ${
          isSmall ? "w-[22px] h-[20px]" : "w-[28px] h-[28px]"
        }`}
      >
        {theme === "light" && (
          <motion.div
            layoutId="activeThemeIndicator"
            className="absolute inset-0 rounded-full bg-white dark:bg-zinc-800 shadow-[0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.35)] border border-neutral-200/10 dark:border-zinc-700/30 -z-10"
            transition={{
              type: "spring",
              stiffness: 380,
              damping: 30,
            }}
          />
        )}
        <motion.div
          whileHover={{ rotate: 15, scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className={`flex items-center justify-center ${
            theme === "light"
              ? "text-amber-500 font-medium scale-105"
              : "text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300"
          }`}
        >
          <HugeiconsIcon
            icon={Sun01Icon}
            size={isSmall ? 13 : 16}
            className="transition-transform duration-200"
          />
        </motion.div>
      </button>

      {/* System Mode Button */}
      <button
        onClick={() => setTheme("system")}
        aria-label="Switch to System Mode"
        className={`relative z-10 flex items-center justify-center rounded-full transition-colors cursor-pointer group ${
          isSmall ? "w-[22px] h-[20px]" : "w-[28px] h-[28px]"
        }`}
      >
        {theme === "system" && (
          <motion.div
            layoutId="activeThemeIndicator"
            className="absolute inset-0 rounded-full bg-white dark:bg-zinc-800 shadow-[0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.35)] border border-neutral-200/10 dark:border-zinc-700/30 -z-10"
            transition={{
              type: "spring",
              stiffness: 380,
              damping: 30,
            }}
          />
        )}
        <motion.div
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className={`flex items-center justify-center ${
            theme === "system"
              ? "text-blue-500 dark:text-blue-400 font-medium scale-105"
              : "text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300"
          }`}
        >
          <SystemIcon
            size={isSmall ? 12 : 15}
            className="transition-transform duration-200"
          />
        </motion.div>
      </button>

      {/* Dark Mode Button */}
      <button
        onClick={() => setTheme("dark")}
        aria-label="Switch to Dark Mode"
        className={`relative z-10 flex items-center justify-center rounded-full transition-colors cursor-pointer group ${
          isSmall ? "w-[22px] h-[20px]" : "w-[28px] h-[28px]"
        }`}
      >
        {theme === "dark" && (
          <motion.div
            layoutId="activeThemeIndicator"
            className="absolute inset-0 rounded-full bg-white dark:bg-zinc-800 shadow-[0_2px_8px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.35)] border border-neutral-200/10 dark:border-zinc-700/30 -z-10"
            transition={{
              type: "spring",
              stiffness: 380,
              damping: 30,
            }}
          />
        )}
        <motion.div
          whileHover={{ rotate: -15, scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          className={`flex items-center justify-center ${
            theme === "dark"
              ? "text-violet-400 font-medium scale-105"
              : "text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300"
          }`}
        >
          <HugeiconsIcon
            icon={Moon02Icon}
            size={isSmall ? 13 : 16}
            className="transition-transform duration-200"
          />
        </motion.div>
      </button>
    </div>
  );
};

export { isDark };
export default DarkModeSwitch;
